import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { User, LogOut, AlertCircle, X, Menu } from 'lucide-react';

function EmployeeDashboard() {
  const [employeeData, setEmployeeData] = useState(null);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkUserAndLoadData = async () => {
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        if (!user) {
          navigate('/employee-login', { replace: true });
          return;
        }

        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const role = userDoc.data().role;
            if (role === 'employee') {
              setEmployeeData({
                uid: user.uid,
                name: userDoc.data().name || user.displayName || '',
                email: userDoc.data().email || user.email,
                role: role,
                bio: userDoc.data().bio || '',
                salary: userDoc.data().salary || '',
                joiningDate: userDoc.data().joiningDate || '',
              });
            } else if (role === 'admin') {
              navigate('/admin-dashboard', { replace: true });
            } else {
              setError('Invalid user role.');
              await auth.signOut();
              navigate('/employee-login', { replace: true });
            }
          } else {
            setError('User data not found.');
            await auth.signOut();
            navigate('/employee-login', { replace: true });
          }
        } catch (err) {
          setError('Unable to load your data. Please try again.');
        } finally {
          setLoading(false);
        }
      });
      return () => unsubscribe();
    };
    checkUserAndLoadData();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/employee-login', { replace: true });
    } catch (err) {
      setError('Unable to log out. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!employeeData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">{error || 'Unable to load employee data'}</p>
          <button
            onClick={() => navigate('/employee-login', { replace: true })}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  const navigationItems = [
    { id: 'profile', label: 'Profile', icon: User },
  ];

  // Format salary as currency (assuming USD for simplicity)
  const formattedSalary = employeeData.salary
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(employeeData.salary)
    : 'Not provided';

  // Format joining date
  const formattedJoiningDate = employeeData.joiningDate
    ? new Date(employeeData.joiningDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'Not provided';

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <div className={`${sidebarOpen ? 'w-64' : 'w-16'} bg-white shadow-lg transition-all duration-300 flex flex-col`}>
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <h1 className="text-xl font-bold text-gray-800">Employee Panel</h1>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={`p-2 rounded-lg hover:bg-gray-100 transition-colors ${!sidebarOpen ? 'mx-auto' : ''}`}
            >
              <Menu className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        <nav className={`flex-1 ${sidebarOpen ? 'p-4' : 'p-2'}`}>
          <div className="space-y-2">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.id} className="relative group">
                  <button
                    className={`w-full flex items-center justify-center py-3 rounded-lg transition-colors bg-blue-100 text-blue-700 border border-blue-200 ${
                      sidebarOpen ? 'px-4' : 'px-2'
                    }`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {sidebarOpen && <span className="ml-3 font-medium">{item.label}</span>}
                  </button>
                  {!sidebarOpen && (
                    <div className="absolute left-16 top-1/2 transform -translate-y-1/2 bg-gray-800 text-white text-sm rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                      {item.label}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        <div className={`border-t border-gray-200 ${sidebarOpen ? 'p-4' : 'p-2'}`}>
          {sidebarOpen && employeeData && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-800">{employeeData.name}</p>
              <p className="text-sm text-gray-500">{employeeData.email}</p>
            </div>
          )}
          <div className="relative group">
            <button
              onClick={handleLogout}
              className={`w-full flex items-center justify-center py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors ${
                sidebarOpen ? 'px-4' : 'px-2'
              }`}
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="ml-3 font-medium">Logout</span>}
            </button>
            {!sidebarOpen && (
              <div className="absolute left-16 top-1/2 transform -translate-y-1/2 bg-gray-800 text-white text-sm rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                Logout
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        <div className="p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Welcome back, {employeeData?.name || 'User'}
            </h2>
            <p className="text-gray-600">View your profile information</p>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <span className="text-red-700 flex-1">{error}</span>
              <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Your Information</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="font-medium text-gray-700">Name:</span>
                  <p className="text-gray-900">{employeeData.name || 'Not provided'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Email:</span>
                  <p className="text-gray-900">{employeeData.email || 'Not provided'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Role:</span>
                  <p className="text-gray-900 capitalize">{employeeData.role || 'Not specified'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Salary:</span>
                  <p className="text-gray-900">{employeeData.salary}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Joining Date:</span>
                  <p className="text-gray-900">{formattedJoiningDate}</p>
                </div>
                {/* <div>
                  <span className="font-medium text-gray-700">User ID:</span>
                  <p className="text-gray-900 text-sm font-mono">{employeeData.uid}</p>
                </div> */}
              </div>
              {/* <div>
                <span className="font-medium text-gray-700">Bio:</span>
                <p className="text-gray-900 mt-1">{employeeData.bio || 'No bio provided'}</p>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmployeeDashboard;